import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FieldTypeModel } from '../../models/field-type.model';

/** Lo que resuelve el modal al guardar. */
export interface IFieldTypeModalResult {
  fieldType: string;
  description: string;
  protected: boolean;
  multiline: boolean;
  status: boolean;
}

/**
 * Alta y edicion de un tipo de campo.
 *
 * `fieldType` es la PK natural del catalogo: se captura al crear y se muestra
 * en solo lectura al editar, porque el API no la actualiza.
 *
 * Se abre con `NgbModal.open(FieldTypeModalComponent)` y luego `init(...)`;
 * ng-bootstrap solo expone `componentInstance`, no hay `setInput`.
 */
@Component({
  selector: 'app-field-type-modal',
  imports: [ReactiveFormsModule],
  templateUrl: './field-type-modal.component.html',
  styleUrl: './field-type-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldTypeModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);

  /** `null` = alta. Con modelo = edicion. */
  protected readonly fieldType = signal<FieldTypeModel | null>(null);

  protected readonly isEdit = computed(() => this.fieldType() !== null);

  protected readonly title = computed(() =>
    this.isEdit() ? 'Editar tipo de campo' : 'Nuevo tipo de campo',
  );

  protected readonly form = this.formBuilder.nonNullable.group({
    // Minusculas, digitos, guion y guion bajo: es un identificador, no una
    // etiqueta visible. La etiqueta legible vive en `description`.
    fieldType: [
      '',
      [Validators.required, Validators.maxLength(50), Validators.pattern(/^[a-z0-9_-]+$/)],
    ],
    description: ['', [Validators.required, Validators.maxLength(255)]],
    protected: [false],
    multiline: [false],
    status: [true],
  });

  protected readonly submitted = signal(false);

  /** Carga el modal. `null` abre el formulario en modo alta. */
  init(fieldType: FieldTypeModel | null): void {
    this.fieldType.set(fieldType);

    if (!fieldType) return;

    this.form.patchValue({
      fieldType: fieldType.fieldType,
      description: fieldType.description,
      protected: fieldType.protected,
      multiline: fieldType.multiline,
      status: fieldType.status,
    });

    // La PK no se actualiza: se muestra, pero no se edita.
    this.form.controls.fieldType.disable();
  }

  protected invalid(control: 'fieldType' | 'description'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || this.submitted());
  }

  protected save(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const current = this.fieldType();

    const result: IFieldTypeModalResult = {
      fieldType: current ? current.fieldType : value.fieldType.trim().toLowerCase(),
      description: value.description.trim(),
      protected: value.protected,
      multiline: value.multiline,
      status: value.status,
    };

    this.activeModal.close(result);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
